/**
 * PhotoFrame — profile/publisher photo that fills its parent, with graceful fallback.
 *
 * @layer-1 — the avatar analog of CoverArt. Use for user/publisher photos that can
 * 404 or be corrupt after an upload.
 *
 * Flow:
 *   - url present & loads OK              → <img object-cover>
 *   - url missing                         → `fallback` (initials letter / icon)
 *   - url present but fails (404/corrupt) → `fallback` + red "broken" marker
 *
 * Parent owns sizing/rounding/`overflow-hidden`; PhotoFrame fills it (w-full h-full).
 */
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { useBrokenImage } from "@/shared/hooks/useBrokenImage";
import { BrokenImageBadge } from "./BrokenImageBadge";

interface PhotoFrameProps {
  /** Photo URL. Empty/undefined renders the fallback. */
  url?: string | null;
  /** Alt text — typically the person/company name. */
  alt?: string;
  /** Shown when no url or the image fails to load (initials letter, icon, etc.). */
  fallback: ReactNode;
  /** Extra classes on the <img>. */
  imgClassName?: string;
  /** Use the corner-dot broken marker (for small avatars). */
  compactBadge?: boolean;
}

export function PhotoFrame({ url, alt, fallback, imgClassName, compactBadge }: PhotoFrameProps) {
  const { showImage, broken, onError } = useBrokenImage(url);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {showImage ? (
        <img
          src={url ?? undefined}
          alt={alt ?? ""}
          className={cn("w-full h-full object-cover", imgClassName)}
          onError={onError}
        />
      ) : (
        fallback
      )}
      {broken && <BrokenImageBadge compact={compactBadge} />}
    </div>
  );
}
